import {
  safeGetItem,
  safeSetItem,
  safeRemoveItem,
} from 'Utils/safeLocalStorage';
import api from '../../api';

const state = {
  showModal: false,
  isHeroModalOpen: false,
  postId: null,
  isLoggedIn: false,
  isEditMode: false,
  deceasedImage: null,
  heroImageSources: null,
  heroImageFallback: null,
  isUploadingDeceasedImage: false,
  isUploadingHeroImage: false,
  isSavingMemorialFund: false,
  isSavingDateText: false,
  heroText: null,
  firstName: '',
  lastName: '',
  nickname: '',
  yearOfBirth: '',
  deceasedDate: '',
  savedHeroText: null,
  isSavingText: false,
  isPublished: false,
  defaultHeroImages: [],
  availableFunds: [],
  gettingAvailableFunds: false,
  memorialFund: null,
  memorialFundLogo: null,
};

const getHash = () => {
  let hash = safeGetItem('creator_hash');

  if (!hash) {
    return '';
  }

  if (hash.endsWith('/')) {
    hash = hash.slice(0, -1);
  }

  return hash;
};

const mutations = {
  setState(state, payload) {
    Object.keys(payload).forEach((key) => {
      state[key] = payload[key];
    });
  },
};

const actions = {
  async checkHash({ commit, state }) {
    const hash = getHash();

    if (!hash) {
      return;
    }

    try {
      const data = await api.post('check-creator-hash', {
        creatorHash: hash,
        id: state.postId,
      });

      if (data?.status === 'success') {
        commit('setState', { isLoggedIn: true });
        return true;
      }

      return true;
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
  async loadDefaultHeroImages({ commit }) {
    try {
      const result = await api.get('getDefaultBackgroundImages');
      commit('setState', { defaultHeroImages: result });
    } catch (error) {
      console.error(error);
    }
  },
  async getAvailableFunds({ commit }) {
    commit('setState', { gettingAvailableFunds: true });
    try {
      const result = await api.get('get-available-funds');
      commit('setState', { availableFunds: result });
      commit('setState', { gettingAvailableFunds: false });
    } catch (error) {
      commit('setState', { gettingAvailableFunds: false });
      console.error(error);
    }
  },
  async saveMemorialFund({ commit }, fund) {
    commit('setState', { isSavingMemorialFund: true });
    try {
      const result = await api.post('save-memorial-fund', {
        termId: fund.value,
        id: state.postId,
        creatorHash: getHash(),
      });
      commit('setState', { memorialFund: result?.term?.term_id });
      commit('setState', { isSavingMemorialFund: false });
      commit('setState', { memorialFundLogo: result?.term?.image });
    } catch (error) {
      commit('setState', { isSavingMemorialFund: false });
      console.error(error);
    }
  },
  async checkShouldShowModal({ commit, dispatch, state }, { id, isPublished }) {
    const myUrl = new URL(window.location.href);
    const params = myUrl.searchParams;
    let orderHash = params.get('order');

    commit('setState', {
      postId: id,
      isPublished: isPublished,
    });

    if (!orderHash) {
      dispatch('checkLoggedIn');
      return;
    }

    window.history.replaceState({}, '', myUrl.pathname);

    if (orderHash.substr(-1) === '/') {
      orderHash = orderHash.substr(0, orderHash.length - 1);
    }

    safeSetItem('creator_hash', orderHash);

    const isLoggedIn = await dispatch('checkLoggedIn');

    if (isLoggedIn) {
      commit('setState', { showModal: true });
    }
  },
  async checkLoggedIn({ dispatch }) {
    return await dispatch('checkHash');
  },
  logOut({ commit }) {
    safeRemoveItem('creator_hash');
    commit('setState', { isLoggedIn: false });
  },
  async saveNameDate({ commit, state }) {
    const hash = getHash();

    if (!hash) {
      return;
    }

    commit('setState', { isSavingDateText: true });

    try {
      const data = await api.post('save-name-date', {
        creatorHash: hash,
        id: state.postId,
        firstName: state.firstName,
        lastName: state.lastName,
        nickname: state.nickname,
        yearOfBirth: state.yearOfBirth,
        deceasedDate: state.deceasedDate,
      });

      if (data?.status === 'success') {
        commit('setState', { isSavingDateText: false });
      }
    } catch (error) {
      commit('setState', { isSavingDateText: false });
      console.error(error);
      throw error;
    }
  },
  async saveHeroText({ commit, state }) {
    const hash = getHash();

    if (!hash) {
      return;
    }
    commit('setState', { isSavingText: true });

    try {
      const data = await api.post('save-hero-text', {
        creatorHash: hash,
        id: state.postId,
        heroText: state.heroText,
      });

      if (data?.status === 'success') {
        commit('setState', { savedHeroText: state.heroText });
        commit('setState', { isSavingText: false });
      }
    } catch (error) {
      commit('setState', { isSavingText: false });
      console.error(error);
      throw error;
    }
  },
  async publishPage({ commit, state }) {
    const hash = getHash();

    if (!hash) {
      return;
    }

    try {
      const data = await api.post('publish-page', {
        creatorHash: hash,
        id: state.postId,
      });

      if (data?.status === 'success') {
        commit('setState', {
          isPublished: true,
          isEditMode: false,
        });

        if (data?.url) {
          window.history.replaceState({}, '', data.url);
        }
        const draftNotice = document.getElementById(
          'memorial-page-creator__draft-notice'
        );
        draftNotice.parentNode.removeChild(draftNotice);
      }
    } catch (error) {
      console.error(error);
      throw error;
    }
  },
  async uploadImage({ commit, state }, { file, type, url }) {
    if (type === 'hero') {
      commit('setState', { isUploadingHeroImage: true });
    } else {
      commit('setState', { isUploadingDeceasedImage: true });
    }

    const hash = getHash();

    if (!hash) {
      return;
    }

    try {
      // custom fetch for file upload
      const response = await fetch(
        `${lavendlaObituary.apiUrl}upload-image/${state.postId}/${hash}/${type}`,
        {
          method: 'POST',
          body: file || url,
        }
      );

      if (!response.ok) {
        throw new Error(response.statusText);
      }

      const data = await response.json();
      const el = new DOMParser().parseFromString(data.image, 'text/html');
      const img = el.querySelector('img');

      if (!img) {
        commit('setState', { isUploadingHeroImage: false });
        commit('setState', { isUploadingDeceasedImage: false });
        return;
      }

      // preload image
      const image = new Image();
      image.src = img.src;
      image.srcset = img.srcset;

      image.onload = () => {
        if (type === 'hero') {
          const sources = el.querySelectorAll('source');
          commit('setState', { heroImageSources: sources });
          commit('setState', { heroImageFallback: img });
          commit('setState', { isUploadingHeroImage: false });
        } else {
          commit('setState', { deceasedImage: img });
          commit('setState', { isUploadingDeceasedImage: false });
        }
      };
    } catch (error) {
      console.error(error);
      commit('setState', { isUploadingDeceasedImage: false });
      commit('setState', { isUploadingHeroImage: false });
      throw error;
    }
  },

  /**
   * Set a default background image from S3 without re-uploading.
   * This stores a direct reference to the shared S3 asset.
   */
  async setDefaultBackgroundImage({ commit, state }, { storagePath }) {
    commit('setState', { isUploadingHeroImage: true });

    const hash = getHash();

    if (!hash) {
      commit('setState', { isUploadingHeroImage: false });
      return;
    }

    try {
      const data = await api.post('set-default-background', {
        creatorHash: hash,
        id: state.postId,
        path: storagePath,
        type: 'obituary_bg_image',
      });

      if (data?.status === 'success' && data?.image) {
        const el = new DOMParser().parseFromString(data.image, 'text/html');
        const img = el.querySelector('img');

        if (!img) {
          commit('setState', { heroImageFallback: null });
          commit('setState', { heroImageSources: [] });
          commit('setState', { isUploadingHeroImage: false });
          return;
        }

        // Preload image before updating UI
        const image = new Image();
        image.src = img.src;
        image.srcset = img.srcset;

        image.onload = () => {
          const sources = el.querySelectorAll('source');
          commit('setState', { heroImageSources: sources });
          commit('setState', { heroImageFallback: img });
          commit('setState', { isUploadingHeroImage: false });
        };

        image.onerror = () => {
          // Still update the UI even if preload fails
          const sources = el.querySelectorAll('source');
          commit('setState', { heroImageSources: sources });
          commit('setState', { heroImageFallback: img });
          commit('setState', { isUploadingHeroImage: false });
        };
      } else {
        commit('setState', { isUploadingHeroImage: false });
      }
    } catch (error) {
      console.error(error);
      commit('setState', { isUploadingHeroImage: false });
      throw error;
    }
  },
};

export default {
  state,
  mutations,
  actions,
  namespaced: true,
};
